// The (string & {}) keeps autocomplete for the known statuses while allowing any custom string.
export type DataStateStatus = 'idle' | 'loading' | 'loaded' | 'error' | (string & {});

export interface DataState<TData = unknown> {
  status: DataStateStatus;
  data: TData | null;
  error: string | null;
  messages?: string | string[] | null;
}

const DATA_STATE = Symbol('DATA_STATE');

export function createDataState<TData>(
  status: DataState['status'],
  data: TData | null,
  error: string | null = null,
  messages?: string | string[] | null
): DataState<TData> {
  const state: DataState<TData> = { status, data, error, messages };

  return Object.defineProperty(state, DATA_STATE, { value: true });
}

export function isDataState(value: unknown): value is DataState {
  return typeof value === 'object' && value !== null && Object.hasOwn(value, DATA_STATE);
}

export function loadedState<TData>(data: TData): DataState<TData> {
  return createDataState('loaded', data, null);
}

export function emptyState<TData = never>(): DataState<TData> {
  return createDataState<TData>('loaded', null, null);
}

export function idleState<TData = never>(): DataState<TData> {
  return createDataState<TData>('idle', null, null);
}

export function loadingState<TData = never>(): DataState<TData> {
  return createDataState<TData>('loading', null, null);
}

export function errorState<TData = never>(error: string): DataState<TData> {
  return createDataState<TData>('error', null, error);
}

export function isEmpty(value: unknown): boolean {
  if (value == null) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }
  return false;
}
